import React from 'react'
import { Draggable } from '@hello-pangea/dnd'
import { Card, CardContent, Typography, Chip, Box, Avatar } from '@mui/material'
import { Ticket } from '@shared/types/entities'

interface KanbanTicketCardProps {
  ticket: Ticket
  index: number
  onClick: (ticketId: string) => void
}

const getPriorityColor = (priority: string): string => {
  switch (priority) {
    case 'Urgent':
      return '#d32f2f'
    case 'High':
      return '#f57c00'
    case 'Medium':
      return '#fbc02d'
    case 'Low':
      return '#388e3c'
    default:
      return '#757575'
  }
}

const getInitials = (name?: string): string => {
  if (!name) return '?'
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

export const KanbanTicketCard: React.FC<KanbanTicketCardProps> = ({
  ticket,
  index,
  onClick
}) => {
  return (
    <Draggable draggableId={ticket.ticketId} index={index}>
      {(provided, snapshot) => (
        <Card
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          onClick={() => onClick(ticket.ticketId)}
          sx={{
            mb: 1.5,
            cursor: 'pointer',
            borderLeft: `4px solid ${getPriorityColor(ticket.priority)}`,
            backgroundColor: snapshot.isDragging ? 'action.hover' : 'background.paper',
            '&:hover': {
              backgroundColor: 'action.hover',
              boxShadow: 2
            },
            transition: 'all 0.2s ease'
          }}
        >
          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
            {/* Ticket Number and Priority */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="caption" color="text.secondary" fontWeight="medium">
                {ticket.ticketNumber}
              </Typography>
              <Chip
                label={ticket.priority}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.7rem',
                  backgroundColor: getPriorityColor(ticket.priority),
                  color: 'white',
                  fontWeight: 'bold'
                }}
              />
            </Box>

            {/* Subject */}
            <Typography
              variant="body2"
              fontWeight="medium"
              sx={{
                mb: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                lineHeight: 1.4
              }}
            >
              {ticket.subject}
            </Typography>

            {/* Bottom Row: Category & Assigned Agent */}
            <Box display="flex" justifyContent="space-between" alignItems="center">
              {/* Category */}
              {ticket.categoryName && (
                <Chip
                  label={ticket.categoryName}
                  size="small"
                  variant="outlined"
                  sx={{ height: 22, fontSize: '0.7rem' }}
                />
              )}

              {/* Assigned Agent Avatar */}
              {ticket.assignedAgentName && (
                <Avatar
                  sx={{
                    width: 24,
                    height: 24,
                    fontSize: '0.7rem',
                    bgcolor: 'primary.main'
                  }}
                  title={ticket.assignedAgentName}
                >
                  {getInitials(ticket.assignedAgentName)}
                </Avatar>
              )}
            </Box>

            {/* SLA Breach Indicator */}
            {ticket.slaBreached && (
              <Box mt={1}>
                <Chip
                  label="SLA Breached"
                  size="small"
                  color="error"
                  sx={{ height: 20, fontSize: '0.65rem' }}
                />
              </Box>
            )}
          </CardContent>
        </Card>
      )}
    </Draggable>
  )
}
